import React, { useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Box,
  Grid,
  Stack,
  Button,
  TextField,
  MenuItem,
  Typography,
  IconButton,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import {
  PageHeader,
  LoadingState,
  StatusChip,
  DetailCard,
  InfoRow,
  ConfirmDialog,
  FormDialog,
} from '../components';
import {
  useWarehouse,
  useLocations,
  useStockSummary,
  useUpdateWarehouse,
  useDeactivateWarehouse,
  useActivateWarehouse,
  useDeleteWarehouse,
  useCreateLocation,
  useUpdateLocation,
  useDeactivateLocation,
  useActivateLocation,
  useDeleteLocation,
} from '../hooks/useWarehouses';
import { useBusinessUuid } from '../useBusinessUuid';
import {
  colors,
  formatCurrency,
  humanize,
  WAREHOUSE_TYPES,
  LOCATION_TYPES,
} from '../constants';

export default function WarehouseDetail() {
  const { uuid } = useParams();
  const history = useHistory();
  const businessUuid = useBusinessUuid();

  const { data: warehouse, isLoading } = useWarehouse(uuid);
  const { data: locations = [] } = useLocations(uuid);
  const { data: stockSummary = [] } = useStockSummary(uuid);

  const updateWarehouse = useUpdateWarehouse();
  const deactivateWarehouse = useDeactivateWarehouse();
  const activateWarehouse = useActivateWarehouse();
  const deleteWarehouse = useDeleteWarehouse();
  const createLocation = useCreateLocation();
  const updateLocation = useUpdateLocation();
  const deactivateLocation = useDeactivateLocation();
  const activateLocation = useActivateLocation();
  const deleteLocation = useDeleteLocation();

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', type: 'warehouse', contactPhone: '' });
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [locOpen, setLocOpen] = useState(false);
  const [editingLoc, setEditingLoc] = useState(null);
  const [locForm, setLocForm] = useState({ name: '', locationType: 'storage' });
  const [deleteLocUuid, setDeleteLocUuid] = useState(null);

  if (isLoading) return <LoadingState />;
  if (!warehouse) {
    return (
      <>
        <PageHeader title="Not Found" backTo="/inventory-management/warehouses" />
        <Typography sx={{ color: colors.gray500 }}>Item not found</Typography>
      </>
    );
  }

  const openEdit = () => {
    setEditForm({
      name: warehouse.name,
      type: warehouse.type,
      contactPhone: warehouse.contactPhone || '',
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    await updateWarehouse.mutateAsync({
      uuid: warehouse.uuid,
      data: {
        businessUuid,
        name: editForm.name,
        type: editForm.type,
        contactPhone: editForm.contactPhone || undefined,
      },
    });
    setEditOpen(false);
  };

  const toggleActive = async () => {
    if (warehouse.isActive) {
      await deactivateWarehouse.mutateAsync({ uuid: warehouse.uuid, businessUuid });
    } else {
      await activateWarehouse.mutateAsync({ uuid: warehouse.uuid, businessUuid });
    }
  };

  const handleDelete = async () => {
    await deleteWarehouse.mutateAsync({ uuid: warehouse.uuid, businessUuid });
    setDeleteOpen(false);
    history.push('/inventory-management/warehouses');
  };

  const openLocCreate = () => {
    setEditingLoc(null);
    setLocForm({ name: '', locationType: 'storage' });
    setLocOpen(true);
  };

  const openLocEdit = (loc) => {
    setEditingLoc(loc);
    setLocForm({ name: loc.name, locationType: loc.locationType });
    setLocOpen(true);
  };

  const saveLoc = async () => {
    if (!locForm.name.trim()) return;
    if (editingLoc) {
      await updateLocation.mutateAsync({
        uuid: editingLoc.uuid,
        data: { businessUuid, name: locForm.name, locationType: locForm.locationType },
      });
    } else {
      await createLocation.mutateAsync({
        warehouseUuid: uuid,
        data: { businessUuid, name: locForm.name, locationType: locForm.locationType },
      });
    }
    setLocOpen(false);
  };

  const toggleLoc = async (loc) => {
    if (loc.isActive) {
      await deactivateLocation.mutateAsync({ uuid: loc.uuid, businessUuid });
    } else {
      await activateLocation.mutateAsync({ uuid: loc.uuid, businessUuid });
    }
  };

  const handleDeleteLoc = async () => {
    await deleteLocation.mutateAsync({ uuid: deleteLocUuid, businessUuid });
    setDeleteLocUuid(null);
  };

  const address = warehouse.address
    ? [warehouse.address.street, warehouse.address.city, warehouse.address.state, warehouse.address.country]
        .filter(Boolean)
        .join(', ')
    : '-';

  return (
    <>
      <PageHeader
        title={warehouse.name}
        subtitle={warehouse.code}
        backTo="/inventory-management/warehouses"
        actions={
          <>
            <StatusChip status={warehouse.type} />
            <StatusChip status={warehouse.isActive ? 'active' : 'inactive'} />
          </>
        }
      />

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={5}>
          <DetailCard
            title="Details"
            action={
              <Button size="small" startIcon={<EditOutlinedIcon />} onClick={openEdit} sx={{ textTransform: 'none' }}>
                Edit
              </Button>
            }
          >
            <InfoRow label="Address" value={address} />
            <InfoRow label="Phone" value={warehouse.contactPhone} />
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                color={warehouse.isActive ? 'warning' : 'success'}
                onClick={toggleActive}
                sx={{ textTransform: 'none' }}
              >
                {warehouse.isActive ? 'Deactivate' : 'Activate'}
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => setDeleteOpen(true)}
                sx={{ textTransform: 'none' }}
              >
                Delete
              </Button>
            </Stack>
          </DetailCard>

          {stockSummary.length > 0 && (
            <Box sx={{ mt: 2.5 }}>
              <DetailCard title="Stock Summary">
                {stockSummary.map((item) => (
                  <Stack
                    key={item.productUuid}
                    direction="row"
                    justifyContent="space-between"
                    sx={{ py: 1, borderBottom: `1px solid ${colors.gray100}` }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.sku}</Typography>
                      <Typography variant="caption" sx={{ color: colors.gray400 }}>{item.productType}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{item.totalQuantity}</Typography>
                      <Typography variant="caption" sx={{ color: colors.gray400 }}>
                        {formatCurrency(item.totalValue)}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
              </DetailCard>
            </Box>
          )}
        </Grid>

        <Grid item xs={12} md={7}>
          <DetailCard
            title={`Locations (${locations.length})`}
            action={
              <Button size="small" startIcon={<AddIcon />} onClick={openLocCreate} sx={{ textTransform: 'none' }}>
                Add
              </Button>
            }
          >
            {locations.length === 0 ? (
              <Typography variant="body2" sx={{ color: colors.gray400, py: 2 }}>
                No locations yet
              </Typography>
            ) : (
              locations.map((loc) => (
                <Stack
                  key={loc.uuid}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ py: 1, borderBottom: `1px solid ${colors.gray100}` }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: loc.isActive ? colors.gray700 : colors.gray400 }}
                    >
                      {loc.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.gray500 }}>
                      {loc.locationPath || loc.code}
                    </Typography>
                  </Box>
                  <StatusChip status={loc.isActive ? loc.locationType : 'inactive'} />
                  <IconButton size="small" onClick={() => openLocEdit(loc)}>
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color={loc.isActive ? 'warning' : 'success'} onClick={() => toggleLoc(loc)}>
                    {loc.isActive ? <PauseCircleOutlineIcon fontSize="small" /> : <PlayCircleOutlineIcon fontSize="small" />}
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => setDeleteLocUuid(loc.uuid)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))
            )}
          </DetailCard>
        </Grid>
      </Grid>

      <FormDialog
        open={editOpen}
        title="Edit Warehouse"
        onClose={() => setEditOpen(false)}
        onSubmit={saveEdit}
        loading={updateWarehouse.isPending}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField label="Name" value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} fullWidth required size="small" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select label="Type" value={editForm.type} onChange={(e) => setEditForm((f) => ({ ...f, type: e.target.value }))} fullWidth size="small">
              {WAREHOUSE_TYPES.map((t) => (
                <MenuItem key={t} value={t}>{humanize(t)}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Phone" value={editForm.contactPhone} onChange={(e) => setEditForm((f) => ({ ...f, contactPhone: e.target.value }))} fullWidth size="small" />
          </Grid>
        </Grid>
      </FormDialog>

      <FormDialog
        open={locOpen}
        title={editingLoc ? 'Edit Location' : 'Add Location'}
        onClose={() => setLocOpen(false)}
        onSubmit={saveLoc}
        loading={createLocation.isPending || updateLocation.isPending}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField label="Name" value={locForm.name} onChange={(e) => setLocForm((f) => ({ ...f, name: e.target.value }))} fullWidth required size="small" />
          </Grid>
          <Grid item xs={12}>
            <TextField select label="Type" value={locForm.locationType} onChange={(e) => setLocForm((f) => ({ ...f, locationType: e.target.value }))} fullWidth size="small">
              {LOCATION_TYPES.map((t) => (
                <MenuItem key={t} value={t}>{humanize(t)}</MenuItem>
              ))}
            </TextField>
          </Grid>
          {!editingLoc && (
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ color: colors.gray400 }}>
                Code will be auto-generated
              </Typography>
            </Grid>
          )}
        </Grid>
      </FormDialog>

      <ConfirmDialog
        open={deleteOpen}
        title="Delete Warehouse"
        message={`Delete "${warehouse.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={deleteWarehouse.isPending}
        onConfirm={handleDelete}
        onClose={() => setDeleteOpen(false)}
      />

      <ConfirmDialog
        open={!!deleteLocUuid}
        title="Delete Location"
        message="Are you sure you want to delete this location?"
        confirmLabel="Delete"
        destructive
        loading={deleteLocation.isPending}
        onConfirm={handleDeleteLoc}
        onClose={() => setDeleteLocUuid(null)}
      />
    </>
  );
}
